import { useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';

import { Button } from '../../ui/button';
import { ConfirmDialog } from '../../ui/confirm-dialog';
import { InlineAlert } from '../../ui/inline-alert';
import { CreateSowDialog } from './sowDialogs';
import { AdminCreateTeamModal } from '../AdminCreateTeamModal';
import { AdminEditCompanyModal } from '../AdminCreateCompanyModal';
import { AdminEditUserModal } from '../AdminEditUserModal';
import { AdminToast } from '../AdminToast';
import { CompanyDetailHeader } from './CompanyDetailHeader';
import { CompanyDetailEmptyState } from './CompanyDetailPrimitives';
import { CompanyHistoricalLedgerImportDialog } from './CompanyHistoricalLedgerImportDialog';
import {
    CompanyDetailTabBar,
    CompanyPulseSubscriptionsTab,
    CompanySowsTab,
    CompanyTeamsTab,
    CompanyUsersTab,
} from './CompanyDetailTabPanels';
import { PulseSubscriptionDialog } from './PulseSubscriptionDialog';
import { COMPANY_USERS_PAGE_SIZE, useCompanyDetail } from './useCompanyDetail';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';

export function CompanyDetail() {
    const detail = useCompanyDetail();
    const [syncConfirm, setSyncConfirm] = useState<'org' | 'org-users' | 'users' | null>(null);
    useDocumentTitle(detail.companyDisplayName);

    if (!detail.numericCompanyId) {
        return (
            <CompanyDetailEmptyState message="This company ID isn't valid. Go back to Companies and choose a company." />
        );
    }

    const activeCompanyMemberUserIds = new Set<number>();
    for (const team of detail.teamsQuery.data?.teams ?? []) {
        if (team.isActive === false) {
            continue;
        }
        for (const member of team.members) {
            const userId = Number(member.user.id);
            if (member.membershipActive !== false && Number.isFinite(userId)) {
                activeCompanyMemberUserIds.add(userId);
            }
        }
    }

    return (
        <div className="space-y-6">
            <CompanyDetailHeader
                companyId={detail.companyId}
                companyDisplayName={detail.companyDisplayName}
                companyIsActive={detail.companyQuery.data?.isActive}
                companyHasProAccess={detail.companyHasProAccess}
                companyHasPulseAccess={detail.companyHasPulseAccess}
                companyLoaded={Boolean(detail.companyQuery.data)}
                onBack={detail.goBackToAdmin}
                onEditCompany={() => detail.setIsEditCompanyOpen(true)}
                isEditDisabled={!detail.companyQuery.data}
                ssoDetailState={detail.ssoDetailState}
                teamsLoaded={detail.teamsQuery.isSuccess}
                isCompanySyncedToAuth0={detail.isCompanySyncedToAuth0}
                isCompanyAuth0Syncing={detail.isCompanyAuth0Syncing}
                canSyncCompanyUsers={detail.canSyncCompanyUsers}
                onSyncCompany={(syncUsers) => setSyncConfirm(syncUsers ? 'org-users' : 'org')}
                onOpenHistoricalImport={() => detail.setIsHistoricalImportOpen(true)}
            />

            {detail.companyQuery.isError ? (
                <InlineAlert
                    tone="error"
                    title="Couldn't load this company's details"
                    onRetry={() => void detail.companyQuery.refetch()}
                    isRetrying={detail.companyQuery.isFetching}
                >
                    {detail.companyQuery.error instanceof Error
                        ? detail.companyQuery.error.message
                        : null}
                </InlineAlert>
            ) : null}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CompanyDetailTabBar
                    activeTab={detail.activeTab}
                    sowCount={detail.sowsQuery.data ? detail.sowRows.length : undefined}
                    pulseSubscriptionCount={
                        detail.pulseSubscriptionsQuery.data?.subscriptions.length
                    }
                    teamCount={detail.teamsQuery.data?.teams.length}
                    userCount={detail.companyUsersQuery.data?.total}
                    onSelectTab={detail.selectActiveTab}
                />
                <div className="flex shrink-0 justify-end gap-2">
                    {detail.activeTab === 'sows' && (
                        <Button type="button" onClick={() => detail.setIsCreateSowOpen(true)}>
                            <Plus aria-hidden />
                            Add SOW
                        </Button>
                    )}
                    {detail.activeTab === 'pulse-subscriptions' && (
                        <Button type="button" onClick={detail.openCreatePulseSubscription}>
                            <Plus aria-hidden />
                            Add Pulse subscription
                        </Button>
                    )}
                    {detail.activeTab === 'teams' && (
                        <Button type="button" onClick={() => detail.setIsCreateTeamOpen(true)}>
                            <Plus aria-hidden />
                            New team
                        </Button>
                    )}
                    {detail.activeTab === 'users' && (
                        <Button
                            type="button"
                            variant="destructiveOutline"
                            disabled={
                                detail.syncUsersToAuth0.isPending ||
                                detail.syncingUserId !== null ||
                                !detail.canSyncCompanyUsers
                            }
                            title={
                                detail.canSyncCompanyUsers
                                    ? undefined
                                    : 'Requires administrate:users permission'
                            }
                            loading={detail.syncUsersToAuth0.isPending}
                            onClick={() => setSyncConfirm('users')}
                        >
                            {!detail.syncUsersToAuth0.isPending && <RefreshCw aria-hidden />}
                            {detail.syncUsersToAuth0.isPending ? 'Syncing…' : 'Sync all unsynced'}
                        </Button>
                    )}
                </div>
            </div>

            <div
                role="tabpanel"
                id="company-detail-panel"
                aria-labelledby={`company-tab-${detail.activeTab}`}
                className="space-y-6"
            >
                {detail.activeTab === 'sows' && (
                    <CompanySowsTab
                        rows={detail.sowRows}
                        isLoading={detail.sowsQuery.isLoading}
                        isError={detail.sowsQuery.isError}
                        onRetry={() => void detail.sowsQuery.refetch()}
                        isRetrying={detail.sowsQuery.isFetching}
                        onRowClick={detail.handleSowRowClick}
                    />
                )}

                {detail.activeTab === 'pulse-subscriptions' && (
                    <CompanyPulseSubscriptionsTab
                        subscriptions={detail.pulseSubscriptionsQuery.data?.subscriptions ?? []}
                        isPending={detail.pulseSubscriptionsQuery.isPending}
                        isError={detail.pulseSubscriptionsQuery.isError}
                        onRetry={() => void detail.pulseSubscriptionsQuery.refetch()}
                        isRetrying={detail.pulseSubscriptionsQuery.isFetching}
                        onRowClick={detail.openEditPulseSubscription}
                    />
                )}

                {detail.activeTab === 'teams' && (
                    <CompanyTeamsTab
                        teams={detail.teamsQuery.data?.teams ?? []}
                        isLoading={detail.teamsQuery.isLoading}
                        isError={detail.teamsQuery.isError}
                        onRetry={() => void detail.teamsQuery.refetch()}
                        isRetrying={detail.teamsQuery.isFetching}
                        expandedTeamIds={detail.expandedTeamIds}
                        inlineTeamEdit={detail.inlineTeamEdit}
                        inlineTeamNameInputRef={detail.inlineTeamNameInputRef}
                        onToggleExpanded={detail.toggleExpandedTeam}
                        onStartInlineEdit={detail.startInlineTeamEdit}
                        onInlineTeamDraftChange={detail.updateInlineTeamDraft}
                        onFinishInlineTeamNameEdit={detail.finishInlineTeamNameEdit}
                        onCancelInlineTeamEdit={detail.cancelInlineTeamNameEdit}
                        onSetTeamActive={detail.handleSetTeamActive}
                        onSetMembershipActive={detail.handleSetMembershipActive}
                        teamActivePendingTeamId={detail.teamActivePendingTeamId}
                        teamMembershipLocked={detail.teamMembershipLocked}
                        teamMembershipPending={detail.teamMembershipPending}
                    />
                )}

                {detail.activeTab === 'users' && (
                    <CompanyUsersTab
                        isError={detail.companyUsersQuery.isError}
                        isPending={detail.companyUsersQuery.isPending}
                        onRetry={() => void detail.companyUsersQuery.refetch()}
                        isRetrying={detail.companyUsersQuery.isFetching}
                        data={detail.companyUsersQuery.data}
                        page={detail.usersPage}
                        pageSize={COMPANY_USERS_PAGE_SIZE}
                        searchDraft={detail.usersSearchDraft}
                        searchQuery={detail.usersSearchQuery}
                        onSearchChange={detail.setUsersSearchDraft}
                        onSearchCommit={detail.commitUsersSearch}
                        onPageChange={detail.setUsersPage}
                        onRowClick={detail.setSelectedUser}
                        onSync={detail.handleSyncUser}
                        syncingUserId={detail.syncingUserId}
                        companyAdminStatusByUserId={detail.companyAdminStatusByUserId}
                        isCompanyAdminStatusPending={detail.companyAdminsQuery.isPending}
                        isCompanyAdminStatusError={detail.companyAdminsQuery.isError}
                        activeCompanyMemberUserIds={activeCompanyMemberUserIds}
                        companyAdminPendingUserId={detail.companyAdminPendingUserId}
                        onSetCompanyUserAdmin={(userId, isAdmin) =>
                            void detail.handleSetCompanyUserAdmin(userId, isAdmin)
                        }
                        organizationId={detail.auth0OrganizationId || null}
                    />
                )}
            </div>

            <CreateSowDialog
                companyId={detail.numericCompanyId}
                open={detail.isCreateSowOpen}
                onClose={() => detail.setIsCreateSowOpen(false)}
                onCreated={() => detail.showSuccessToast('SOW created.')}
                pricingConfigs={detail.pricingQuery.data?.pricingConfigs ?? []}
                pulseSubscriptionTiers={detail.pulseSubscriptionTiersQuery.data?.tiers ?? []}
                teams={(detail.teamsQuery.data?.teams ?? [])
                    .filter((team) => team.isActive !== false)
                    .map((team) => ({ id: Number(team.id), name: team.name }))
                    .filter((team) => Number.isFinite(team.id))}
                teamsLoading={detail.teamsQuery.isPending}
            />
            <CompanyHistoricalLedgerImportDialog
                open={detail.isHistoricalImportOpen}
                onClose={() => detail.setIsHistoricalImportOpen(false)}
                companyId={detail.numericCompanyId}
                companyName={detail.companyDisplayName}
                pricingConfigs={detail.pricingQuery.data?.pricingConfigs ?? []}
                onApplied={(result) => {
                    detail.showSuccessToast('Historical SOW imported.');
                    detail.handleSowRowClick({ id: result.sowId, name: 'Historical SOW' });
                }}
            />
            <PulseSubscriptionDialog
                open={detail.isPulseSubscriptionDialogOpen}
                subscription={detail.selectedPulseSubscription}
                companyName={detail.companyDisplayName}
                teams={(detail.teamsQuery.data?.teams ?? [])
                    .filter((team) => team.isActive !== false)
                    .map((team) => ({ id: Number(team.id), name: team.name }))
                    .filter((team) => Number.isSafeInteger(team.id) && team.id > 0)}
                tiers={detail.pulseSubscriptionTiersQuery.data?.tiers ?? []}
                isSaving={
                    detail.pulseSubscriptionMutations.createSubscription.isPending ||
                    detail.pulseSubscriptionMutations.updateSubscription.isPending
                }
                onClose={detail.closePulseSubscriptionDialog}
                onSave={detail.savePulseSubscription}
            />
            <AdminEditUserModal
                open={detail.selectedUser !== null}
                user={detail.selectedUser}
                onClose={() => detail.setSelectedUser(null)}
                onSaved={(result) =>
                    result.auth0SyncWarning
                        ? detail.showWarningToast(result.auth0SyncWarning)
                        : detail.showSuccessToast('User updated.')
                }
            />
            <AdminCreateTeamModal
                open={detail.isCreateTeamOpen}
                companyId={detail.numericCompanyId}
                onClose={() => detail.setIsCreateTeamOpen(false)}
                onCreated={() => detail.showSuccessToast('Team created.')}
            />
            <AdminEditCompanyModal
                open={detail.isEditCompanyOpen}
                company={
                    detail.companyQuery.data
                        ? {
                              companyId: detail.numericCompanyId,
                              name: detail.companyQuery.data.name,
                              isActive: detail.companyQuery.data.isActive,
                              customizations: detail.companyQuery.data.customizations,
                          }
                        : null
                }
                onClose={() => detail.setIsEditCompanyOpen(false)}
                onSaved={() => detail.showSuccessToast('Company updated.')}
            />
            <AdminToast toast={detail.toast} onClose={detail.dismissToast} />
            <ConfirmDialog
                open={syncConfirm !== null}
                title={
                    syncConfirm === 'org'
                        ? 'Sync company organization to Auth0?'
                        : syncConfirm === 'org-users'
                          ? 'Sync company organization and users to Auth0?'
                          : 'Sync all unsynced users to Auth0?'
                }
                description={
                    syncConfirm === 'org'
                        ? `${detail.companyDisplayName} will be synced to Auth0.`
                        : syncConfirm === 'org-users'
                          ? `${detail.companyDisplayName} and its unsynced users will be synced to Auth0.`
                          : `All unsynced users in ${detail.companyDisplayName} will be synced to Auth0.`
                }
                confirmLabel={
                    syncConfirm === 'org'
                        ? 'Sync organization'
                        : syncConfirm === 'org-users'
                          ? 'Sync organization and users'
                          : 'Sync users'
                }
                pendingLabel="Syncing…"
                destructive
                onCancel={() => setSyncConfirm(null)}
                onConfirm={async () => {
                    const kind = syncConfirm;
                    if (kind === 'users') {
                        await detail.handleSyncAllUnsyncedUsers();
                    } else if (kind) {
                        await detail.handleSyncCompanyToAuth0(kind === 'org-users');
                    }
                    setSyncConfirm(null);
                }}
            />
        </div>
    );
}
